/**
 * Hardware Data Mappers
 *
 * Utilities to convert GraphQL types to existing interfaces used throughout the app.
 * This allows gradual migration from REST API to GraphQL while maintaining
 * compatibility with existing components.
 */

import type { HardwareDetailGraphQL } from '../hooks/hardware/query/V2/useHardwareDetailRelay';
import type { HardwareGraphQL } from '../hooks/hardware/query/V2/useHardwareRelay';
import type { RentalGraphQL } from '../hooks/hardware/query/V2/useHardwareRentalsRelay';
import type { MaintenanceTicketGraphQL } from '../hooks/hardware/query/V2/useHardwareTicketsRelay';
import type { MaintenanceLogGraphQL } from '../hooks/hardware/query/V2/useHardwareLogsRelay';
import type { IHardware, IHardwareType } from '../ts/interface/hardware.interface';
import type { IRental } from '../ts/interface/rental.interface';
import type { ITicket, ITask } from '../ts/interface/problem.interface';

/**
 * Maps a GraphQL Hardware detail to the legacy IHardware interface
 *
 * Note: Some fields from REST API may not be available in GraphQL and will have default values.
 * Additional REST-only fields can be fetched separately when needed.
 */
export function mapGraphQLToIHardware(
  hw: HardwareDetailGraphQL | HardwareGraphQL,
  rentals?: RentalGraphQL[],
  tickets?: MaintenanceTicketGraphQL[],
  logs?: MaintenanceLogGraphQL[]
): Partial<IHardware> & { id: string } {
  const hardwareTypeName = hw.hardwareType?.name ?? '';
  const primaryIdentifier = getPrimaryIdentifier(hw);

  return {
    id: hw.id,
    gid: hw.id,
    old_id: hw.oldId ?? '',

    // Hardware identifiers
    mac: hw.mac ?? null,
    imei: hw.imei ?? null,
    number: hw.number ?? 0,
    label: (hw as HardwareDetailGraphQL).label ?? null,

    // Type information
    hardware_type_name: hardwareTypeName,
    type_name: hardwareTypeName,
    hardware_type: hw.hardwareType?.id ?? '',
    hardwareTypes: hw.hardwareType ? [hw.hardwareType.id] : [],
    primary_identifier: primaryIdentifier,

    // Product information
    hardware_product_name: (hw as HardwareDetailGraphQL).hardwareProduct?.name ?? null,
    product_name: (hw as HardwareDetailGraphQL).hardwareProduct?.name ?? null,
    version_name: (hw as HardwareDetailGraphQL).hardwareProductVersion?.name ?? null,
    hardware_product_id:
      (hw as HardwareDetailGraphQL).hardwareProductId ??
      (hw as HardwareGraphQL).hardwareProductId,
    hardware_product_version_id:
      (hw as HardwareDetailGraphQL).hardwareProductVersionId ??
      (hw as HardwareGraphQL).hardwareProductVersionId ??
      null,

    // Status
    state: hw.state ?? 0,
    available: hw.isAvailable ?? false,
    unlocked_cached: hw.isUnlocked ?? null,

    // Location
    current_station_id: hw.locationId ?? null,
    current_station: hw.locationOldId ?? '',
    station: hw.locationOldId ?? '',
    station_name: hw.location?.name ?? '',
    last_lat: hw.lat ?? null,
    last_lng: hw.lng ?? null,
    latitude: hw.lat ?? null,
    longitude: hw.lng ?? null,
    has_gps: (hw as HardwareDetailGraphQL).hardwareProduct?.hasLocationTracking ?? false,

    // Battery status
    batteryStatus:
      hw.batteryPercent !== null || hw.batteryVoltage !== null
        ? [
            {
              battery_percent: hw.batteryPercent,
              battery_voltage: hw.batteryVoltage,
              battery_charging: hw.charging,
            },
          ]
        : [],
    has_battery_status:
      (hw as HardwareDetailGraphQL).hardwareProduct?.hasBatteryStatus ?? false,

    // Rental status
    current_rental: hw.activeRentalId ?? null,
    current_rental_gid: hw.activeRentalId ?? null,

    // Remote capabilities (from HardwareDetailGraphQL)
    supports_remote_unlock:
      (hw as HardwareDetailGraphQL).hardwareProduct?.supportsRemoteUnlock ?? false,
    supports_remote_lock:
      (hw as HardwareDetailGraphQL).hardwareProduct?.supportsRemoteUnlock ?? false,
    supports_remote_ring:
      (hw as HardwareDetailGraphQL).hardwareProduct?.supportsRemoteRing ?? false,
    supports_remote_location_check:
      (hw as HardwareDetailGraphQL).hardwareProduct?.supportsRemoteLocationCheck ?? false,
    supports_remote_battery_unlock:
      (hw as HardwareDetailGraphQL).hardwareProduct?.supportsRemoteBatteryUnlock ?? false,
    supports_remote_battery_lock:
      (hw as HardwareDetailGraphQL).hardwareProduct?.supportsRemoteBatteryLock ?? false,

    // TCP/Connection status
    remote_connection_async: hw.isOnline ?? null,

    // Vendor fields
    vendor_id: (hw as HardwareDetailGraphQL).vendorId ?? null,
    vendor_qr_code: (hw as HardwareDetailGraphQL).vendorQrCode ?? null,
    vendor_serial: (hw as HardwareDetailGraphQL).vendorSerial ?? null,

    // ICCID and firmware
    iccid: (hw as HardwareDetailGraphQL).iccid ?? null,
    firmware_version: (hw as HardwareDetailGraphQL).firmwareVersion ?? null,

    // Last seen
    cached_last_seen: hw.lastSeen ?? null,

    // Timestamps
    createdOn: hw.createdOn,

    // Parent/Child relationships
    parents: mapParentsToLegacy((hw as HardwareDetailGraphQL).parentHardwareAssignments),
    children: mapChildrenToLegacy((hw as HardwareDetailGraphQL).childHardwareAssignments),

    // Nested data
    rentals: rentals ? mapRentalsToLegacy(rentals) : [],
    tickets: tickets ? mapTicketsToLegacy(tickets) : [],
    logs: logs ? mapLogsToLegacy(logs) : [],

    // Maintenance counts
    openMaintenanceTickets: (hw as HardwareDetailGraphQL).openMaintenanceTickets ?? 0,
    overdueMaintenanceTickets:
      (hw as HardwareDetailGraphQL).overdueMaintenanceTickets ?? 0,

    // Safety ticket - mapped from GraphQL hasOpenSafetyTicket
    has_safety_ticket: (hw as HardwareDetailGraphQL).hasOpenSafetyTicket ?? false,

    // Display name
    display_name: getDisplayName(hw),

    // Module (for compatibility)
    module: '',
  } as Partial<IHardware> & { id: string };
}

/**
 * Get primary identifier type based on hardware product settings
 */
function getPrimaryIdentifier(
  hw: HardwareDetailGraphQL | HardwareGraphQL
): 'number' | 'imei' | 'mac' {
  const product = (hw as HardwareDetailGraphQL).hardwareProduct;
  if (product?.primaryIdentifier) {
    return product.primaryIdentifier as 'number' | 'imei' | 'mac';
  }
  // Fallback based on available identifiers
  if (hw.number !== null && hw.number !== undefined) return 'number';
  if (hw.imei) return 'imei';
  if (hw.mac) return 'mac';
  return 'number';
}

/**
 * Get display name for hardware
 */
function getDisplayName(hw: HardwareDetailGraphQL | HardwareGraphQL): string {
  const typeName = hw.hardwareType?.name ?? 'Hardware';
  if (hw.number !== null && hw.number !== undefined) {
    return `${typeName} ${hw.number}`;
  }
  if ((hw as HardwareDetailGraphQL).label) {
    return (hw as HardwareDetailGraphQL).label!;
  }
  if (hw.imei) return hw.imei;
  if (hw.mac) return hw.mac;
  return typeName;
}

/**
 * Map parent hardware assignments to legacy format
 */
function mapParentsToLegacy(
  parentAssignments: HardwareDetailGraphQL['parentHardwareAssignments']
): Partial<IHardware>[] {
  if (!parentAssignments || parentAssignments.length === 0) return [];

  return parentAssignments
    .filter((assignment) => assignment.parent)
    .map((assignment) => {
      const parent = assignment.parent!;
      return {
        id: assignment.id,
        gid: parent.id,
        parent_id: parent.id,
        parent_hardware_type_name: parent.hardwareType?.name ?? '',
        parent_hardware_type: parent.hardwareType?.id ?? '',
        number: parent.number ?? 0,
        mac: parent.mac ?? null,
        imei: parent.imei ?? null,
        vendor_id: parent.vendorId ?? null,
        vendor_serial: parent.vendorSerial ?? null,
        label: parent.label ?? null,
        primary_identifier: parent.hardwareProduct?.primaryIdentifier ?? 'number',
        created_on: assignment.createdOn,
        removed_on: assignment.removedOn ?? null,
        child: false,
      } as Partial<IHardware>;
    });
}

/**
 * Map child hardware assignments to legacy format
 */
function mapChildrenToLegacy(
  childAssignments: HardwareDetailGraphQL['childHardwareAssignments']
): Partial<IHardware>[] {
  if (!childAssignments || childAssignments.length === 0) return [];

  return childAssignments
    .filter((assignment) => assignment.child)
    .map((assignment) => {
      const child = assignment.child!;
      const primaryIdentifier = child.hardwareProduct?.primaryIdentifier ?? 'number';
      return {
        id: assignment.id,
        gid: child.id,
        child_id: child.id,
        old_id: child.oldId ?? '',
        number: child.number ?? 0,
        mac: child.mac ?? null,
        imei: child.imei ?? null,
        vendor_id: child.vendorId ?? null,
        vendor_serial: child.vendorSerial ?? null,
        label: child.label ?? null,
        state: child.state ?? 0,
        hardware_type_name: child.hardwareType?.name ?? '',
        child_hardware_type_name: child.hardwareType?.name ?? '',
        child_hardware_type: child.hardwareType?.id ?? '',
        primary_identifier: primaryIdentifier,
        created_on: assignment.createdOn,
        removed_on: assignment.removedOn ?? null,
        child: true,
      } as Partial<IHardware>;
    });
}

/**
 * Map GraphQL rentals to legacy IRental format
 */
export function mapRentalsToLegacy(rentals: RentalGraphQL[]): Partial<IRental>[] {
  return rentals.map((rental) => ({
    id: rental.id,
    gid: rental.id,
    old_id: rental.oldId,
    startTime: rental.startTime,
    start_time: rental.startTime,
    duration: rental.duration,
    chargeAmount: rental.chargeAmount,
    charge_amount: rental.chargeAmount,
    rating: rental.rating,
    distance: rental.distance,
    completed: rental.completed,
    user_id: rental.userId,
    system_id: rental.systemId,
    // Fields expected by hardwareRentalColumnsV2
    start_station: rental.startLocation?.name ?? null,
    end_station: rental.endLocation?.name ?? null,
    end_time: rental.completed ? rental.startTime : null, // endTime not directly available
  })) as Partial<IRental>[];
}

/**
 * Map GraphQL tickets to legacy ITicket format
 */
export function mapTicketsToLegacy(
  tickets: MaintenanceTicketGraphQL[]
): Partial<ITicket>[] {
  return tickets.map((ticket) => ({
    id: ticket.id,
    gid: ticket.id,
    old_id: ticket.oldId ?? '',
    due_on: ticket.dueOn,
    completed_on: ticket.completedOn ?? null,
    canceled_on: ticket.canceledOn ?? null,
    created_on: ticket.createdOn,
    description: ticket.description,
    priority: ticket.priority,
    status: ticket.status ?? '',
    assignee: ticket.assignee ?? '',
    reporter: ticket.reporter ?? '',
    source_type: ticket.sourceType,
    maintenance_task_id: ticket.maintenanceTaskId,
    hardware_id: ticket.hardwareOldId ?? '',
    tags: ticket.tags ? [...ticket.tags] : [],
  })) as Partial<ITicket>[];
}

/**
 * Map GraphQL logs to legacy ITask format
 */
export function mapLogsToLegacy(logs: MaintenanceLogGraphQL[]): Partial<ITask>[] {
  return logs.map((log) => ({
    id: log.id,
    gid: log.id,
    old_id: log.oldId ?? '',
    created_on: log.createdOn,
    notes: log.notes ?? '',
    time_tracking: log.timeTracking,
    admin_user_id: log.adminUserId,
    admin_user_email: log.adminUser?.email ?? '',
    admin_user_name: log.adminUser
      ? `${log.adminUser.firstName ?? ''} ${log.adminUser.lastName ?? ''}`.trim()
      : '',
    // Fields expected by taskColumnsV2
    admin_name: log.adminUser
      ? `${log.adminUser.firstName ?? ''} ${log.adminUser.lastName ?? ''}`.trim()
      : '',
    task_name: log.maintenanceTask?.name ?? '',
    hardware_id: log.hardwareOldId ?? '',
    maintenance_task_id: log.maintenanceTaskOldId,
    maintenance_task_name: log.maintenanceTask?.name ?? '',
    maintenance_ticket_id: log.maintenanceTicket?.id ?? '',
    station_gid: log.locationId ?? '',
    station_name: log.location?.name ?? '',
  })) as Partial<ITask>[];
}

/**
 * Map HardwareType GraphQL to IHardwareType
 */
export function mapHardwareTypeToLegacy(type: {
  id: string;
  name?: string | null;
}): IHardwareType {
  return {
    id: type.id,
    name: type.name ?? '',
    label: type.name ?? '',
    name_plural: type.name ? `${type.name}s` : '',
  };
}
